import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Button, checkPasswordStrength, PasswordField, PasswordStrength } from 'components/common';
import { colorType } from 'components/common/buttons/Button';
import Head from 'components/common/Head/Head';
import { LoginSignupWrapper } from 'components/styled/Form.styled';
import RegistrationHeader from 'components/styled/RegistrationHeader';
import { BackToLoginLink } from 'modules/account/features/forgotPassword/components';
import { useMutationResetPassword } from 'modules/account/features/forgotPassword/hooks';
import { ButtonSpacing } from 'modules/account/features/singup/components/index.styled';
import { TPasswordStrength } from 'modules/account/features/singup/models';

export const ResetPasswordSchema = Yup.object().shape({
  password: Yup.string().required('Required'),
  passwordConfirm: Yup.string().oneOf([Yup.ref('password'), null], "Passwords don't match"),
});

export const ResetPassword = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [passwordStrength, setPasswordStrength] = useState<TPasswordStrength[]>([]);

  const navigateToLogin = () => {
    navigate('/login');
  };

  const { mutate, status } = useMutationResetPassword(navigateToLogin);

  const resetPasswordSubmitForm = (values: any) => {
    const isValidPassword = passwordStrength.every((item: TPasswordStrength) => item.isValid);
    if (!isValidPassword) return;

    mutate({ ...values, token: token as string });
  };

  return (
    <>
      <Head title={t('Reset password')} />
      <RegistrationHeader>{t('Reset password')}</RegistrationHeader>
      <Formik
        initialValues={{
          password: '',
          passwordConfirm: '',
        }}
        validationSchema={ResetPasswordSchema}
        validate={(values: any) => {
          const { password } = values;
          const passwordValidation = checkPasswordStrength(password);
          setPasswordStrength(passwordValidation);
        }}
        onSubmit={resetPasswordSubmitForm}
      >
        {({ errors, touched }) => (
          <LoginSignupWrapper>
            <PasswordField
              mb="0.8rem"
              label="New password"
              name="password"
              isRequired
              error={errors['password'] && touched['password']}
            />
            <PasswordStrength conditionValues={passwordStrength} />

            <PasswordField
              mt="1.25rem"
              label="Repeat new password"
              name="passwordConfirm"
              isRequired
              error={errors['passwordConfirm'] && touched['passwordConfirm']}
            />

            <ButtonSpacing>
              <Button
                type="submit"
                label="Create"
                color="blue"
                width="100%"
                isFetching={status === 'loading'}
              />
            </ButtonSpacing>
          </LoginSignupWrapper>
        )}
      </Formik>
    </>
  );
};
